/**
 * Grundgerüst: Sidebar, mobiles Menü, Inhaltsbereich, Toast.
 *
 * Die Navigation zeigt nur freigeschaltete Bereiche. Eine Gruppe ohne
 * sichtbaren Punkt verschwindet samt Überschrift.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { ChatVersorgung } from './ChatKontext';
import { useKora } from '../zustand';
import { Wortmarke } from './Wortmarke';
import { Farbpanel } from '../modals/FarbenModal';
import {
  IconAbmelden,
  IconAdmin,
  IconBeleg,
  IconBurger,
  IconGluehbirne,
  IconAuftrag,
  IconFinanzen,
  IconKalenderblatt,
  IconHaken,
  IconKunden,
  IconPalette,
  IconProfil,
  IconSchloss,
  IconTag,
  IconVertrieb,
} from './Icons';

interface NavPunkt {
  key: string;
  pfad: string;
  label: ReactNode;
  titel: string;
  icon: ReactNode;
  /** Bereichsschlüssel; null bedeutet: immer sichtbar */
  bereich: string | null;
  schloss?: boolean;
}

interface NavGruppe {
  titel: string;
  /** Sensibler Bereich – das Schloss steht am Gruppentitel. */
  schloss?: boolean;
  punkte: NavPunkt[];
}

/*
 * Dieselben Wörter überall (Mein Tag v6, Richtlinie 15): „Mein Tag" ist der
 * Ausschnitt, „Vorgänge" sind die Gesamtlisten, „Auswertungen" ist Geld. Das
 * Schloss sitzt am Gruppentitel, nicht an jedem Punkt.
 *
 * Warum die Umbenennung: „Auftragsübersicht" und „Auftragsabwicklung" klangen
 * wie dieselbe Sache mit und ohne Schloss. Jetzt heißt die Arbeitsliste
 * schlicht „Aufträge" und die Auswertung „Auftragsauswertung".
 */
const GRUPPEN: NavGruppe[] = [
  {
    titel: '',
    punkte: [
      {
        key: 'start',
        pfad: '/',
        titel: 'Mein Tag',
        label: 'Mein Tag',
        icon: <IconTag />,
        bereich: null,
      },
    ],
  },
  {
    titel: 'Vorgänge',
    punkte: [
      {
        key: 'monitor',
        pfad: '/cockpit',
        titel: 'Angebote',
        label: 'Angebote',
        icon: <IconVertrieb />,
        bereich: 'monitor',
      },
      {
        key: 'abwicklung',
        pfad: '/abwicklung',
        titel: 'Aufträge',
        label: 'Aufträge',
        icon: <IconAuftrag />,
        bereich: null,
      },
      /*
       * Der Montagekalender steht unter den Aufträgen (MONTAGEKALENDER.md
       * 1.1): Er ist deren Fortsetzung – geliefert, dann montiert.
       */
      {
        key: 'montage',
        pfad: '/montagekalender',
        titel: 'Montagekalender',
        label: 'Montagekalender',
        icon: <IconKalenderblatt />,
        bereich: 'montage',
      },
    ],
  },
  {
    titel: 'Kunden',
    punkte: [
      {
        key: 'kunden',
        pfad: '/kunden',
        titel: 'Kundenstamm',
        label: 'Kundenstamm',
        icon: <IconKunden />,
        bereich: 'kunden',
      },
    ],
  },
  {
    titel: 'Auswertungen',
    schloss: true,
    punkte: [
      {
        key: 'auftraege',
        pfad: '/auftraege',
        titel: 'Auftragsauswertung',
        label: 'Auftragsauswertung',
        icon: <IconAuftrag />,
        bereich: 'auftraege',
      },
      {
        key: 'finanzen',
        pfad: '/finanzen',
        titel: 'Finanzen',
        label: 'Finanzen',
        icon: <IconFinanzen />,
        bereich: 'finanzen',
      },
    ],
  },
  {
    titel: 'Buchhaltung',
    schloss: true,
    punkte: [
      {
        key: 'buchhaltung',
        pfad: '/buchhaltung',
        titel: 'Buchhaltungsvorbereitung',
        label: 'Buchhaltungsvorbereitung',
        icon: <IconBeleg />,
        bereich: 'buchhaltung',
      },
    ],
  },
];

export function Shell({ children }: { children: ReactNode }) {
  const { nutzer, darf, menuOffen, menuSchliessen } = useKora();
  const ort = useLocation();

  useEffect(() => {
    menuSchliessen();
    // Beim Seitenwechsel schließt sich das mobile Menü.
  }, [ort.pathname, menuSchliessen]);

  /*
   * Esc schließt die Schublade (v13, 6.4). Sie liegt über dem Inhalt; wer
   * sie versehentlich öffnet, soll sie ohne Zielen wieder loswerden – die
   * Tastatur ist der kürzeste Weg dorthin.
   */
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') menuSchliessen();
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [menuSchliessen]);

  const gruppen = GRUPPEN.map((g) => ({
    ...g,
    punkte: g.punkte.filter((p) => p.bereich === null || darf(p.bereich)),
  })).filter((g) => g.punkte.length > 0);

  return (
    <ChatVersorgung>
    <div className="eh-shell">
      <div
        className={`eh-scrim ${menuOffen ? 'eh-scrim--an' : ''}`}
        onClick={menuSchliessen}
        aria-hidden="true"
      />

      <aside className={`eh-side ${menuOffen ? 'eh-side--offen' : ''}`}>
        <div className="eh-brand">
          <div className="eh-brand-mini">K</div>
          <div className="eh-brand-full">
            <img src="/eichenherz-logo.png" alt="Eichenherz" className="eh-brand-logo" />
            <div className="eh-wortmarke"><Wortmarke /></div>
          </div>
        </div>

        {/*
          „+ Neues Angebot" stand hier und ist entfallen.

          Ein Angebot legt man dort an, wo Angebote stehen – in der
          Kopfzeile der Angebotsübersicht. Im Menü war es eine zweite Tür,
          die auf jeder Maske Platz kostete und Navigation mit Aktion
          vermischte. Das Menü enthält jetzt ausschließlich Navigation.
        */}

        <nav className="eh-nav">
          {gruppen.map((gruppe) => (
            <div className="eh-nav-gruppe" key={gruppe.titel || 'haupt'}>
              {gruppe.titel && (
                <div className="eh-nav-titel eh-navtext">
                  {gruppe.titel}
                  {gruppe.schloss && (
                    <span
                      className="eh-nav-titel-schloss"
                      title="Sensibler Bereich – Zugriff nur für freigeschaltete Personen"
                    >
                      <IconSchloss groesse={11} />
                    </span>
                  )}
                </div>
              )}
              {gruppe.punkte.map((punkt) => (
                <NavLink
                  key={punkt.key}
                  to={punkt.pfad}
                  end={punkt.pfad === '/'}
                  title={punkt.titel}
                  aria-label={punkt.titel}
                  className={({ isActive }) =>
                    `eh-nav-punkt ${isActive ? 'eh-nav-punkt--aktiv' : ''}`
                  }
                >
                  <span className="eh-nav-icon">{punkt.icon}</span>
                  <span className="eh-nav-label eh-navtext">{punkt.label}</span>
                  {punkt.schloss && (
                    <span
                      className="eh-nav-schloss eh-navtext"
                      title="Sensibler Bereich – Zugriff nur für freigeschaltete Personen"
                    >
                      <IconSchloss />
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <Vorschlagspunkt />

        {nutzer && <Profilblock />}
      </aside>

      <main className="eh-main">
        {children}
      </main>

      <Toast />
    </div>
    </ChatVersorgung>
  );
}

/**
 * „Verbesserungsvorschläge" – abgesetzt über dem Profilblock.
 *
 * Bewusst nicht in den Arbeitsgruppen: Es ist eine Meta-Funktion zum Werkzeug
 * selbst, kein Arbeitsbereich. Sichtbar für alle, ohne Schloss.
 *
 * Der rote Zähler zeigt unbeantwortete Vorschläge und nur dem Administrator –
 * für alle anderen wäre er Dauerdekoration, weil sie nichts entscheiden können.
 * Er ist die Benachrichtigung im Werkzeug; die Mail geht zusätzlich hinaus.
 */
function Vorschlagspunkt() {
  const { nutzer } = useKora();

  const anzahl = useQuery({
    queryKey: ['vorschlaege-anzahl'],
    queryFn: () => api.hole<{ offen: number }>('/vorschlaege/anzahl'),
    enabled: !!nutzer?.istAdmin,
    staleTime: 60_000,
  });

  const offen = nutzer?.istAdmin ? (anzahl.data?.offen ?? 0) : 0;

  return (
    <div className="eh-nav-meta">
      <div className="eh-nav-titel eh-navtext">Kora</div>
      <NavLink
        to="/vorschlaege"
        title="Verbesserungsvorschläge"
        aria-label="Verbesserungsvorschläge"
        className={({ isActive }) => `eh-nav-punkt ${isActive ? 'eh-nav-punkt--aktiv' : ''}`}
      >
        <span className="eh-nav-icon">
          <IconGluehbirne />
        </span>
        <span className="eh-nav-label eh-navtext">Verbesserungsvorschläge</span>
        {offen > 0 && (
          <span
            className="eh-nav-zaehler eh-navtext"
            title={`${offen} unbeantwortete${offen === 1 ? 'r Vorschlag' : ' Vorschläge'}`}
          >
            {offen}
          </span>
        )}
      </NavLink>
    </div>
  );
}

function Profilblock() {
  const { nutzer, modalOeffnen, abmelden } = useKora();
  const [offen, setOffen] = useState(false);
  /* „Farben meiner Rubriken" steht als Panel über dem Profil (v13, 13.1). */
  const [farbenOffen, setFarbenOffen] = useState(false);
  const block = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    function ausserhalb(e: MouseEvent) {
      if (block.current && !block.current.contains(e.target as Node)) setOffen(false);
    }
    document.addEventListener('mousedown', ausserhalb);
    return () => document.removeEventListener('mousedown', ausserhalb);
  }, [offen]);

  if (!nutzer) return null;

  return (
    <div className="eh-profilblock" ref={block}>
      {farbenOffen && <Farbpanel onSchliessen={() => setFarbenOffen(false)} />}

      {offen && (
        <div className="eh-profilmenue" role="menu">
          <button
            type="button"
            onClick={() => {
              setOffen(false);
              modalOeffnen({ art: 'profil' });
            }}
          >
            <span style={{ display: 'flex', color: 'var(--eh-text-4)', flex: 'none' }}>
              <IconProfil />
            </span>
            <span style={{ flex: 1 }}>Profil</span>
          </button>

          {/* Persönliche Vorliebe, deshalb hier und nicht im Admin Center. */}
          <button
            type="button"
            onClick={() => {
              setOffen(false);
              setFarbenOffen(true);
            }}
          >
            <span style={{ display: 'flex', color: 'var(--eh-text-4)', flex: 'none' }}>
              <IconPalette />
            </span>
            <span style={{ flex: 1 }}>Farben meiner Rubriken</span>
          </button>

          {nutzer.istAdmin && (
            <>

              <button
                type="button"
                onClick={() => {
                  setOffen(false);
                  modalOeffnen({ art: 'adminCenter' });
                }}
              >
                <span style={{ display: 'flex', color: 'var(--eh-text-4)', flex: 'none' }}>
                  <IconAdmin />
                </span>
                <span style={{ flex: 1 }}>Admin Center</span>
              </button>
            </>
          )}

          <div className="eh-profilmenue-trenner" />

          <button type="button" className="eh-abmelden" onClick={() => void abmelden()}>
            <span style={{ display: 'flex', color: 'var(--eh-rot)', flex: 'none' }}>
              <IconAbmelden />
            </span>
            <span style={{ flex: 1 }}>Abmelden</span>
          </button>
        </div>
      )}

      <button
        type="button"
        className={`eh-profilknopf ${offen ? 'eh-profilknopf--offen' : ''}`}
        title="Profil und Abmelden"
        onClick={() => setOffen((a) => !a)}
      >
        <span
          className="eh-avatar"
          style={{
            width: 34,
            height: 34,
            fontSize: 12.5,
            ...(nutzer.profilbild ? { backgroundImage: `url(${nutzer.profilbild})` } : {}),
          }}
        >
          {nutzer.profilbild ? '' : nutzer.initialen}
        </span>
        <span
          className="eh-navtext"
          style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, flex: 1, minWidth: 0 }}
        >
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>{nutzer.name}</span>
          <span
            style={{
              fontSize: 11.5,
              color: 'var(--eh-text-3)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nutzer.rolle}
          </span>
        </span>
        <span className="eh-navtext" style={{ fontSize: 11, color: 'var(--eh-text-3)', flex: 'none' }}>
          {offen ? '▾' : '▴'}
        </span>
      </button>
    </div>
  );
}

function Toast() {
  const { toast, toastSchliessen } = useKora();
  if (!toast) return null;
  return (
    <div className="eh-toast" role="status" onClick={toastSchliessen}>
      <IconHaken />
      <span>{toast}</span>
    </div>
  );
}

/**
 * Der Menü-Knopf der Kopfzeile (v13, 6.4 und 7.1).
 *
 * Er steht links vor dem Titel, nicht schwebend in der Ecke: Ein Knopf, der
 * über dem Inhalt klebt, verdeckt die erste Zeile jeder Maske und sieht auf
 * jeder anders platziert aus. Über 1400 px ist er ausgeblendet – dort steht
 * das Menü ohnehin.
 *
 * Dieselbe Komponente auf jeder Maske, wie der Nachrichten-Knopf: gleiche
 * Funktion, gleiche Umsetzung.
 */
export function Menueknopf() {
  const { menuOffen, menuUmschalten } = useKora();
  return (
    <button
      type="button"
      className="eh-menueknopf"
      onClick={menuUmschalten}
      title={menuOffen ? 'Menü schließen' : 'Menü öffnen'}
      aria-label={menuOffen ? 'Menü schließen' : 'Menü öffnen'}
      aria-expanded={menuOffen}
    >
      <IconBurger />
    </button>
  );
}
